"use client";

import { ESystemRoles } from "@constants/SystemRoles";
import { IMenuList } from "components/Layout/ManagerProLayout/utils";
import { EPrivateRouteType } from "components/SideMenu/side-menu-routes";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { useVendor } from "context/vendor/vendor.provider";
import useTranslation from "next-translate/useTranslation";
import { PRIVATE_ROUTES } from "routes/routes";
import { ICONS_MODULES_PATHS } from "./sidebar-icon-modules-path";
import { resolveMagazineName } from "components/ui/v2/magazine/magazine-utils";

/**
 *
 * @returns IMenuList[]
 * @description This function returns the main menu list for the sidebar
 * @example
 * ```tsx
 * import MainMenuList from "components/Layout/Sidebar/components/body/content/utils/menuList";
 *
 * const Menu: React.FC = () => {
 *  const menuItems = MainMenuList();
 *
 *  return (
 *    <ul>
 *      {menuItems.map((item, index) => {
 *        if (item.type === EPrivateRouteType.DROPDOWN) {
 *          return <MenuDropdown key={index} item={item} />;
 *        } else {
 *          return <MenuItem key={index} item={item} />;
 *        }
 *      })}
 *    </ul>
 *  );
 *};
 *
 *export default Menu;
 *```
 *
 */
const MainMenuList = (): IMenuList[] => {
  const { t } = useTranslation("common");
  const { user } = useUserStoreContext((store) => ({
    user: store.user
  }));
  const { vendor } = useVendor();
  const magazineName = resolveMagazineName(vendor, t("Magazine"));

  // ADMIN e ISI ven todos los menús independientemente de flags de usuario específicos
  const isAdminOrIsi =
    user?.isi === true || user?.systemRole === ESystemRoles.ADMIN;

  let MAIN_MENU_LISTS: IMenuList[] = [
    {
      name: t("Aprendizaje"),
      moduleIcon: "evaluations",
      urlIcon: ICONS_MODULES_PATHS.LEARNING.EVALUATIONS_MODULE,
      type: EPrivateRouteType.DROPDOWN,
      children: [
        {
          name: t("Evaluaciones"),
          moduleIcon: "appreciation",
          urlIcon: ICONS_MODULES_PATHS.LEARNING.APPRECIATION,
          route: PRIVATE_ROUTES.appreciationList,
          type: EPrivateRouteType.ITEM
        },
        {
          name: t("Mis evaluaciones"),
          moduleIcon: "my-evaluations",
          urlIcon: ICONS_MODULES_PATHS.LEARNING.MY_EVALUATIONS,
          route: PRIVATE_ROUTES.myAppreciations,
          type: EPrivateRouteType.ITEM
        },
        {
          name: t("Mis recursos"),
          moduleIcon: "resources",
          urlIcon: ICONS_MODULES_PATHS.LEARNING.RESOURCES,
          route: PRIVATE_ROUTES.userMultimedias,
          type: EPrivateRouteType.ITEM
        },
        !vendor?.enableJobyfine
          ? {
              name: t("Preselección"),
              moduleIcon: "jobyfine-preselection",
              urlIcon: ICONS_MODULES_PATHS.JOBYFINE.JOBYFINE_PRESELECTION,
              route: PRIVATE_ROUTES.publicAppreciation,
              type: EPrivateRouteType.ITEM
            }
          : [],
        vendor?.enableCertificates
          ? {
              name: t("Mis certificados"),
              moduleIcon: "certificate",
              urlIcon: ICONS_MODULES_PATHS.CONFIG.CERTIFICATE,
              route: PRIVATE_ROUTES.userCertificates,
              type: EPrivateRouteType.ITEM
            }
          : []
      ].filter(Boolean) as IMenuList[]
    },
    {
      name: t("Radar empresarial"),
      moduleIcon: "radar",
      urlIcon: ICONS_MODULES_PATHS.RADAR.RADAR_MODULE,
      route: PRIVATE_ROUTES.radar,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Mis equipos"),
      route: PRIVATE_ROUTES.teamsLeader,
      moduleIcon: "teams",
      urlIcon: ICONS_MODULES_PATHS.RADAR.TEAMS,
      type: EPrivateRouteType.ITEM
    },
    {
      render: vendor?.enablePerformanceEvaluation,
      name: t("Eval de desempeño"),
      route: PRIVATE_ROUTES.listPerformanceEvaluation,
      moduleIcon: "file-badge",
      urlIcon: ICONS_MODULES_PATHS.SURVEYS.LIST_PERFORMANCE_EVALUATION,
      type: EPrivateRouteType.ITEM
    }
  ];

  if (vendor?.enableExplore) {
    MAIN_MENU_LISTS = [
      {
        name: t("Inicio"),
        moduleIcon: "explore",
        urlIcon: ICONS_MODULES_PATHS.EXPLORE.EXPLORE_MODULE,
        route: PRIVATE_ROUTES.explore,
        type: EPrivateRouteType.ITEM
      },
      ...MAIN_MENU_LISTS
    ];
  }

  if (vendor?.enableChallenges) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Challenges"),
        moduleIcon: "challenges",
        urlIcon: ICONS_MODULES_PATHS.CHALLENGES.CHALLENGE_GROUP,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Mis desafios"),
            moduleIcon: "user-challenges",
            urlIcon: ICONS_MODULES_PATHS.CHALLENGES.USER_CHALLENGES,
            route: PRIVATE_ROUTES.questionsIndex,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Crear desafios"),
            moduleIcon: "create-challenges",
            urlIcon: ICONS_MODULES_PATHS.CHALLENGES.CREATE_CHALLENGES,
            route: PRIVATE_ROUTES.challenge,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Ranking"),
            moduleIcon: "ranking",
            urlIcon: ICONS_MODULES_PATHS.CHALLENGES.RANKING,
            route: PRIVATE_ROUTES.dailyQuestions,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];
  }

  if (vendor?.enableActionPlans) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Planes de acción"),
        moduleIcon: "action-plans",
        urlIcon: ICONS_MODULES_PATHS.ACTION_PLANS.ACTION_PLAN_GROUP,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Mis planes de acción"),
            moduleIcon: "my-action-plan",
            urlIcon: ICONS_MODULES_PATHS.ACTION_PLANS.MY_ACTION_PLAN,
            route: PRIVATE_ROUTES.userActionPlans,
            type: EPrivateRouteType.ITEM
          },

          {
            name: t("Crear plan de acción"),
            moduleIcon: "create-action-plan",
            urlIcon: ICONS_MODULES_PATHS.ACTION_PLANS.CREATE_ACTION_PLAN,
            route: PRIVATE_ROUTES.actionPlans,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];
  }

  if (vendor?.enableCareerPlan) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Ruta Profesional"),
        moduleIcon: "career",
        urlIcon: ICONS_MODULES_PATHS.CAREER_PLAN.CAREER_GROUP,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Mi plan de desarrollo"),
            moduleIcon: "my-development-plan",
            urlIcon: ICONS_MODULES_PATHS.CAREER_PLAN.MY_DEVELOPMENT_PLAN,
            route: PRIVATE_ROUTES.myCareer,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Fases del desarrollo"),
            moduleIcon: "development-phases",
            urlIcon: ICONS_MODULES_PATHS.CAREER_PLAN.DEVELOPMENT_PHASES,
            route: PRIVATE_ROUTES.itemPathMantainers,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Planes de desarrollo"),
            moduleIcon: "development-plans",
            urlIcon: ICONS_MODULES_PATHS.CAREER_PLAN.DEVELOPMENT_PLANS,
            route: PRIVATE_ROUTES.coursesMaintainer,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];
  }

  if (vendor?.enableForms) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Encuestas"),
        moduleIcon: "surveys",
        urlIcon: ICONS_MODULES_PATHS.SURVEYS.SURVEY_GROUP,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Encuestas"),
            moduleIcon: "survey",
            urlIcon: ICONS_MODULES_PATHS.SURVEYS.SURVEY,
            route: PRIVATE_ROUTES.forms,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Mis encuestas"),
            moduleIcon: "my-survey",
            urlIcon: ICONS_MODULES_PATHS.SURVEYS.MY_SURVEY,
            route: PRIVATE_ROUTES.MyForms,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];
  }

  if (vendor?.enableVacations) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Mis vacaciones"),
        moduleIcon: "vacations",
        urlIcon: ICONS_MODULES_PATHS.CONFIG.VACATIONS,
        route: PRIVATE_ROUTES.vacationUser,
        type: EPrivateRouteType.ITEM
      }
    ];
  }


  MAIN_MENU_LISTS = [
    ...MAIN_MENU_LISTS,
  ];

  if (vendor?.enableBuySell) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Compra y venta"),
        moduleIcon: "marketplace",
        urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.MARKETPLACE_GROUP,
        route: PRIVATE_ROUTES.buySell,
        type: EPrivateRouteType.ITEM
      }
    ];
  }

  if (vendor?.enableMagazine) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: magazineName,
        moduleIcon: "canva",
        urlIcon: ICONS_MODULES_PATHS.EXPLORE.CANVA,
        route: PRIVATE_ROUTES.magazine,
        type: EPrivateRouteType.ITEM
      }
    ];
  }

  if (vendor?.enableCoins) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Marketplace"),
        moduleIcon: "marketplace",
        urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.MARKETPLACE_GROUP,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Tienda"),
            moduleIcon: "marketplace",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.MARKETPLACE_GROUP,
            route: PRIVATE_ROUTES.coinMarketplace,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Mi carrito"),
            moduleIcon: "coins",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.COINS_MODULE,
            route: PRIVATE_ROUTES.coinBasket,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Mis canjes"),
            moduleIcon: "exchange-control",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.EXCHANGE_CONTROL,
            route: PRIVATE_ROUTES.coinsExchange,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Ranking"),
            moduleIcon: "coins",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.COINS_GROUP,
            route: PRIVATE_ROUTES.coinsTopUsers,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];
  }

  if (vendor?.enableCoins && (user?.coinsAdmin || isAdminOrIsi)) {
    const COINS_ADMIN_OPTIONS = [
      {
        name: "Coins",
        moduleIcon: "coins",
        urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.COINS_GROUP,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Productos"),
            moduleIcon: "products",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.PRODUCTS,
            route: PRIVATE_ROUTES.coinsProduct,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Categorias de productos"),
            moduleIcon: "products-category",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.PRODUCTS_CATEGORY,
            route: PRIVATE_ROUTES.coinsProductCategory,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Concepto de monedas"),
            moduleIcon: "coins-concept",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.COINS_CONCEPT,
            route: PRIVATE_ROUTES.coinsCategory,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Control de Canjes"),
            moduleIcon: "exchange-control",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.EXCHANGE_CONTROL,
            route: PRIVATE_ROUTES.coinsController,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Actividades"),
            moduleIcon: "activities",
            urlIcon: ICONS_MODULES_PATHS.MARKETPLACE.ACTIVITIES,
            route: PRIVATE_ROUTES.activities,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];

    MAIN_MENU_LISTS = [...MAIN_MENU_LISTS, ...COINS_ADMIN_OPTIONS];
  }

  if (vendor?.enableBenefits) {
    MAIN_MENU_LISTS = [
      ...MAIN_MENU_LISTS,
      {
        name: t("Beneficios"),
        moduleIcon: "benefits",
        urlIcon: ICONS_MODULES_PATHS.BENEFITS.BENEFITS_MODULE,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Solicitudes de beneficios"),
            moduleIcon: "benefit-requests",
            urlIcon: ICONS_MODULES_PATHS.BENEFITS.BENEFIT_REQUESTS,
            route: PRIVATE_ROUTES.benefitRequests,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Portal de Beneficios"),
            moduleIcon: "benefits-portal",
            urlIcon: ICONS_MODULES_PATHS.BENEFITS.BENEFITS_PORTAL,
            route: PRIVATE_ROUTES.benefitsPortal,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];
  }

  if (
    vendor?.enableJobyfine &&
    (user?.jobyfineAdmin ||
      user?.jobyfineRecruiter ||
      user?.jobyfineRequester ||
      isAdminOrIsi)
  ) {
    const JOBYFINE_ADMIN_OPTIONS = [
      {
        name: "Jobyfine",
        moduleIcon: "jobyfine",
        urlIcon: ICONS_MODULES_PATHS.JOBYFINE.JOBYFINE_GROUP,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Dashboard"),
            moduleIcon: "clipboard",
            urlIcon: ICONS_MODULES_PATHS.JOBYFINE.CLIPBOARD,
            route: PRIVATE_ROUTES.jobyfineDashboard,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Solicitudes"),
            moduleIcon: "requests",
            urlIcon: ICONS_MODULES_PATHS.JOBYFINE.REQUESTS,
            route: PRIVATE_ROUTES.jobyfineRequest,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Aplicantes"),
            moduleIcon: "job-applicants",
            urlIcon: ICONS_MODULES_PATHS.JOBYFINE.JOB_APPLICANTS,
            route: PRIVATE_ROUTES.jobyfineApplicants,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Ofertas"),
            moduleIcon: "offers",
            urlIcon: ICONS_MODULES_PATHS.JOBYFINE.OFFERS,
            route: PRIVATE_ROUTES.jobyfineOffers,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Proceso"),
            moduleIcon: "process",
            urlIcon: ICONS_MODULES_PATHS.JOBYFINE.PROCESS,
            route: PRIVATE_ROUTES.jobyfineProcess,
            type: EPrivateRouteType.ITEM
          },

          {
            name: t("Preselección"),
            moduleIcon: "jobyfine-preselection",
            urlIcon: ICONS_MODULES_PATHS.JOBYFINE.JOBYFINE_PRESELECTION,
            route: PRIVATE_ROUTES.publicAppreciation,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Finder"),
            moduleIcon: "finder",
            urlIcon: ICONS_MODULES_PATHS.JOBYFINE.FINDER,
            route: PRIVATE_ROUTES.skillsRequirements,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];

    MAIN_MENU_LISTS = [...MAIN_MENU_LISTS, ...JOBYFINE_ADMIN_OPTIONS];
  }

  // Grupo 1 — Estructura y catálogos (estructura organizacional + catálogos)
  const estructuraChildren = [
    {
      name: t("Usuarios"),
      moduleIcon: "users",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.USERS,
      route: PRIVATE_ROUTES.userMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Cargos"),
      moduleIcon: "position",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.POSITION,
      route: PRIVATE_ROUTES.roleMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Areas"),
      moduleIcon: "area",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.AREA,
      route: PRIVATE_ROUTES.areaMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Divisiones"),
      moduleIcon: "division",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.DIVISION,
      route: PRIVATE_ROUTES.divisionMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Clientes"),
      urlIcon: ICONS_MODULES_PATHS.JOBYFINE.BUILDING,
      route: PRIVATE_ROUTES.jobyfineClientMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Rubros"),
      moduleIcon: "industry",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.INDUSTRY,
      route: PRIVATE_ROUTES.industryMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Equipos"),
      moduleIcon: "teams",
      urlIcon: ICONS_MODULES_PATHS.RADAR.TEAMS,
      route: PRIVATE_ROUTES.teamMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Habilidades"),
      moduleIcon: "skills",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.SKILLS,
      route: PRIVATE_ROUTES.dimensionMaintainer,
      type: EPrivateRouteType.ITEM
    },
    ...(vendor?.enableCorporateValue
      ? [
          {
            name: t("Valores corporativos"),
            moduleIcon: "corporate-values",
            urlIcon: ICONS_MODULES_PATHS.CONFIG.CORPORATE_VALUES,
            route: PRIVATE_ROUTES.corporateValuesMaintainer,
            type: EPrivateRouteType.ITEM
          }
        ]
      : []),
    {
      name: t("Certificaciones"),
      moduleIcon: "certifications",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.CERTIFICATIONS,
      route: PRIVATE_ROUTES.certificates,
      type: EPrivateRouteType.ITEM
    }
  ];

  // Grupo 2 — Configuración (programas, módulos, contenido y ajustes)
  const configChildren = [
    {
      name: t("Org. Dashboard"),
      moduleIcon: "certifications",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.CERTIFICATIONS,
      route: PRIVATE_ROUTES.organizationDashboard,
      type: EPrivateRouteType.ITEM
    },
    ...(vendor?.enablePerformanceEvaluation
      ? [
          {
            name: t("Eval de desempeño"),
            moduleIcon: "performance-evaluation",
            urlIcon: ICONS_MODULES_PATHS.CONFIG.PERFORMANCE_EVALUATION,
            route: PRIVATE_ROUTES.performanceEvaluation,
            type: EPrivateRouteType.ITEM
          }
        ]
      : []),
    ...(vendor?.enableCertificates
      ? [
          {
            name: t("Certificados"),
            moduleIcon: "certificate",
            urlIcon: ICONS_MODULES_PATHS.CONFIG.CERTIFICATE,
            route: PRIVATE_ROUTES.certificates,
            type: EPrivateRouteType.ITEM
          }
        ]
      : []),
    ...(vendor?.enableBenefits
      ? [
          {
            name: t("Beneficios"),
            moduleIcon: "benefits",
            urlIcon: ICONS_MODULES_PATHS.BENEFITS.BENEFITS_MODULE,
            route: PRIVATE_ROUTES.benefitMaintainer,
            type: EPrivateRouteType.ITEM
          }
        ]
      : []),
    ...(vendor?.enableVacations
      ? [
          {
            name: t("Vacaciones"),
            moduleIcon: "vacations",
            urlIcon: ICONS_MODULES_PATHS.CONFIG.VACATIONS,
            route: PRIVATE_ROUTES.vacationManagement,
            type: EPrivateRouteType.ITEM
          }
        ]
      : []),
    ...(vendor?.enableAnonymousBox && (user?.anonymousBoxAdmin || isAdminOrIsi)
      ? [
          {
            name: t("AnonymousBox"),
            moduleIcon: "anonymous-mail",
            urlIcon: ICONS_MODULES_PATHS.CONFIG.ANONYMOUS_MAIL,
            route: PRIVATE_ROUTES.anonybox,
            type: EPrivateRouteType.ITEM
          }
        ]
      : []),
    {
      name: t("Preguntas"),
      moduleIcon: "questions",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.QUESTIONS,
      route: PRIVATE_ROUTES.questionMantainers,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Recursos"),
      moduleIcon: "resources",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.RESOURCES,
      route: PRIVATE_ROUTES.multimediaMaintainer,
      type: EPrivateRouteType.ITEM
    },
    {
      name: t("Ajustes"),
      moduleIcon: "app-setting",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.APP_SETTING,
      route: PRIVATE_ROUTES.configMantainer,
      type: EPrivateRouteType.ITEM
    }
  ];

  const MANTAINERS_AND_CONFIG = [
    {
      name: t("Estructura y catálogos"),
      moduleIcon: "division",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.DIVISION,
      type: EPrivateRouteType.DROPDOWN,
      children: estructuraChildren
    },
    {
      name: t("Configuration"),
      moduleIcon: "config",
      urlIcon: ICONS_MODULES_PATHS.CONFIG.CONFIG_GROUP,
      type: EPrivateRouteType.DROPDOWN,
      children: configChildren
    }
  ];

  // * add config to menu as last item
  MAIN_MENU_LISTS = [...MAIN_MENU_LISTS, ...MANTAINERS_AND_CONFIG];

  if (user?.isi) {
    const ISI_OPTIONS = [
      {
        name: t("Management"),
        moduleIcon: "super-admin",
        urlIcon: ICONS_MODULES_PATHS.SUPER_ADMIN.SUPER_ADMIN_GROUP,
        iconSize: 50,
        route: PRIVATE_ROUTES.activities,
        type: EPrivateRouteType.DROPDOWN,
        children: [
          {
            name: t("Feedback"),
            moduleIcon: "feedback",
            urlIcon: ICONS_MODULES_PATHS.SUPER_ADMIN.FEEDBACK,
            route: PRIVATE_ROUTES.isiFeedback,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Public Challenge"),
            moduleIcon: "public-challenge",
            urlIcon: ICONS_MODULES_PATHS.SUPER_ADMIN.PUBLIC_CHALLENGE,
            route: PRIVATE_ROUTES.publicChallenge,
            type: EPrivateRouteType.ITEM
          },
          {
            name: t("Skills"),
            moduleIcon: "skills",
            urlIcon: ICONS_MODULES_PATHS.CONFIG.SKILLS,
            route: PRIVATE_ROUTES.isiSkills,
            type: EPrivateRouteType.ITEM
          }
        ]
      }
    ];

    MAIN_MENU_LISTS = [...MAIN_MENU_LISTS, ...ISI_OPTIONS];
  }

  return MAIN_MENU_LISTS;
};

export default MainMenuList;
